import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import greenhouseImage from '../../images/groupimage2.jpeg';

import Footer from '../../components/Footer';

const ConsentPage = () => {
  const navigate = useNavigate();
  const [consent1, setConsent1] = useState(false);
  const [consent2, setConsent2] = useState(false);

  const handleNext = () => {
    if (consent1 && consent2) {
      // Navigate to next step in registration process
      // You can update this route as needed
      navigate('/registration');
    }
  };

  return (
    <div className="min-h-screen bg-gray-300 top-0 bottom-0 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-700">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            {/* Left side - Back button and Program name */}
            <div className="flex items-center gap-3 sm:gap-6">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-[#1C4A35] transition-colors"
              >
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-sm ">Back</span>
              </button>
              <h1 className="text-xl sm:text-2xl font-bold text-[#1C4A35]">EYES Ghana</h1>
            </div>

            {/* Right side - Partner logos (same as LandingPage) */}
            <div className="flex items-center justify-center gap-2 sm:gap-4 flex-1 sm:flex-initial sm:justify-end">
              <div className="grid grid-cols-3 gap-3 sm:gap-6 items-center justify-items-center">
                {[
                  { id: 1, src: '/Agra.svg', alt: 'AGRA' },
                  { id: 2, src: '/lfgg.svg', alt: 'LFG' },
                  { id: 3, src: '/legon.svg', alt: 'University of Ghana' },
                ].map((logo) => (
                  <div
                    key={logo.id}
                    className="p-1 sm:p-4 flex items-center justify-center h-10 sm:h-24"
                  >
                    <img
                      src={logo.src}
                      alt={logo.alt}
                      className={`object-contain ${logo.id === 3 ? 'h-10 w-14 sm:h-24 sm:w-32' : 'h-8 w-auto sm:h-16'}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Top Title Bar */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 sm:px-6 py-2">
        <p className="text-gray-300 text-sm">Consent & Registration</p>
      </div>

      {/* Main Content Area */}
      <div className="container mx-auto px-4 sm:px-6 sm:py-8 bg-white justify-center lg:my-12">
        <div className="bg-white rounded-lg overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[500px] sm:min-h-[600px]">
            {/* Left Section - Image */}
            <div className="relative bg-green-800">
              <img
                src={greenhouseImage}
                alt="Youth working in greenhouse"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-green-900/20"></div>
            </div>

            {/* Right Section - Form */}
            <div className="p-6 sm:p-8 md:p-12 flex flex-col justify-center">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                {/* Title */}
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1C4A35] mb-6">
                  Welcome to the EYES Program!
                </h2>

                {/* Information Paragraphs */}
                <div className="space-y-4 mb-6 sm:mb-8 text-gray-700">
                  <p className="text-sm sm:text-base leading-relaxed">
                    Thank you for your time. Your participation is voluntary, your information will be kept confidential and used only for project planning.
                  </p>
                  <p className="text-sm sm:text-base leading-relaxed">
                    This registration is strictly for respondents aged between <span className="font-bold">18 to 35 years</span>.
                  </p>
                </div>

                {/* Consent Checkboxes */}
                <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
                  {/* First Checkbox */}
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={consent1}
                      onChange={(e) => setConsent1(e.target.checked)}
                      className="mt-1 w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 cursor-pointer bg-white"
                    />
                    <span className="text-gray-700 text-xs sm:text-sm leading-relaxed group-hover:text-gray-900">
                      I confirm that all the information I have provided is true and accurate to the best of my knowledge.
                    </span>
                  </label>

                  {/* Second Checkbox */}
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={consent2}
                      onChange={(e) => setConsent2(e.target.checked)}
                      className="mt-1 w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 cursor-pointer bg-white"
                    />
                    <span className="text-gray-700 text-xs sm:text-sm leading-relaxed group-hover:text-gray-900">
                      I consent to the collection & use of my data as described in the EYES Ghana Privacy Policy.
                    </span>
                  </label>
                </div>

                {/* Next Button */}
                <motion.button
                  onClick={handleNext}
                  disabled={!consent1 || !consent2}
                  whileHover={{ scale: consent1 && consent2 ? 1.02 : 1 }}
                  whileTap={{ scale: consent1 && consent2 ? 0.98 : 1 }}
                  className={`w-full py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg transition-all ${consent1 && consent2
                      ? 'bg-green-900 hover:bg-green-700 text-white shadow-lg'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                >
                  Next
                </motion.button>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ConsentPage;